import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Mail, Send, Clock, CheckCircle, XCircle, Loader2, UserPlus, RefreshCw, ChevronDown, ChevronUp, Search, Check, Plus, Trash2, Pencil } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const LaunchCampaignPanel = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [creatingAccounts, setCreatingAccounts] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [message, setMessage] = useState(null);
  const [showGuests, setShowGuests] = useState(false);
  const [selectedEmails, setSelectedEmails] = useState(new Set());
  const [searchFilter, setSearchFilter] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [newGuest, setNewGuest] = useState({ name: '', email: '', profession: '' });
  const [addingGuest, setAddingGuest] = useState(false);
  const [editingGuest, setEditingGuest] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', email: '', profession: '' });

  const fetchStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/launch-campaign/status`, { withCredentials: true });
      setStatus(res.data);
    } catch (err) {
      console.error('Failed to fetch campaign status:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStatus();
    const interval = setInterval(fetchStatus, 10000);
    return () => clearInterval(interval);
  }, [fetchStatus]);

  const guests = status?.guests || [];
  const filteredGuests = guests.filter(g =>
    g.name?.toLowerCase().includes(searchFilter.toLowerCase()) ||
    g.email?.toLowerCase().includes(searchFilter.toLowerCase())
  );

  const toggleEmail = (email) => {
    setSelectedEmails(prev => {
      const next = new Set(prev);
      if (next.has(email)) next.delete(email);
      else next.add(email);
      return next;
    });
  };

  const selectAll = () => {
    if (selectedEmails.size === filteredGuests.length) {
      setSelectedEmails(new Set());
    } else {
      setSelectedEmails(new Set(filteredGuests.map(g => g.email)));
    }
  };

  const createAccounts = async () => {
    setCreatingAccounts(true);
    setMessage(null);
    try {
      const emails = selectedEmails.size > 0 ? Array.from(selectedEmails) : [];
      const res = await axios.post(`${API}/api/launch-campaign/create-accounts`, { emails }, { withCredentials: true });
      setMessage({ type: 'success', text: `${res.data.created} comptes créés, ${res.data.already_exists} existants` });
      fetchStatus();
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.detail || 'Erreur lors de la création des comptes' });
    } finally {
      setCreatingAccounts(false);
    }
  };

  const sendNow = async () => {
    const count = selectedEmails.size > 0 ? selectedEmails.size : guests.length;
    if (!window.confirm(`Envoyer le mail de lancement à ${count} invité(s) MAINTENANT ?`)) return;
    setSending(true);
    setMessage(null);
    try {
      const emails = selectedEmails.size > 0 ? Array.from(selectedEmails) : [];
      const res = await axios.post(`${API}/api/launch-campaign/send-now`, { emails }, { withCredentials: true });
      setMessage({ type: 'success', text: res.data.message });
      fetchStatus();
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.detail || "Erreur lors de l'envoi" });
    } finally {
      setSending(false);
    }
  };

  const retryFailed = async () => {
    setRetrying(true);
    setMessage(null);
    try {
      const res = await axios.post(`${API}/api/launch-campaign/retry-failed`, {}, { withCredentials: true });
      setMessage({ type: 'success', text: res.data.message });
      fetchStatus();
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.detail || 'Erreur' });
    } finally {
      setRetrying(false);
    }
  };

  const addGuest = async (e) => {
    e.preventDefault();
    if (!newGuest.name || !newGuest.email) return;
    setAddingGuest(true);
    setMessage(null);
    try {
      const res = await axios.post(`${API}/api/launch-campaign/add-guest`, newGuest, { withCredentials: true });
      setMessage({ type: res.data.success ? 'success' : 'error', text: res.data.message });
      if (res.data.success) {
        setNewGuest({ name: '', email: '', profession: '' });
        setShowAddForm(false);
        fetchStatus();
      }
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.detail || 'Erreur' });
    } finally {
      setAddingGuest(false);
    }
  };

  const deleteGuest = async (email, name) => {
    if (!window.confirm(`Supprimer ${name} (${email}) de la liste ?`)) return;
    try {
      const res = await axios.post(`${API}/api/launch-campaign/delete-guest`, { email }, { withCredentials: true });
      setMessage({ type: res.data.success ? 'success' : 'error', text: res.data.message });
      fetchStatus();
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.detail || 'Erreur' });
    }
  };

  const startEdit = (g) => {
    setEditingGuest(g.email);
    setEditForm({ name: g.name, email: g.email, profession: g.profession || '' });
  };

  const saveEdit = async (oldEmail) => {
    try {
      const res = await axios.post(`${API}/api/launch-campaign/edit-guest`, { old_email: oldEmail, ...editForm }, { withCredentials: true });
      setMessage({ type: res.data.success ? 'success' : 'error', text: res.data.message });
      setEditingGuest(null);
      fetchStatus();
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.detail || 'Erreur' });
    }
  };

  if (loading) {
    return (
      <Card className="border-blue-200 bg-gradient-to-r from-blue-50 to-indigo-50 mb-8">
        <CardContent className="p-6 flex items-center justify-center">
          <Loader2 className="h-5 w-5 animate-spin text-blue-500 mr-2" />
          <span className="text-slate-600">Chargement...</span>
        </CardContent>
      </Card>
    );
  }

  const emailStats = status?.email_stats || { total: 0, sent: 0, failed: 0, pending: 0 };
  const campaign = status?.campaign;
  const isSending = campaign?.status === 'sending';
  const withAccount = guests.filter(g => g.has_account).length;
  const withoutAccount = guests.length - withAccount;

  return (
    <Card className="border-blue-200 bg-gradient-to-r from-blue-50 to-indigo-50 mb-8" data-testid="launch-campaign-panel">
      <CardContent className="p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-xl bg-blue-100 flex items-center justify-center">
              <Mail className="h-7 w-7 text-blue-600" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-slate-900">Inscriptions Version Free</h3>
              <p className="text-slate-600 text-sm">
                {guests.length} invités | {withAccount} comptes | {withoutAccount} sans compte
                {selectedEmails.size > 0 && <span className="text-blue-600 font-semibold"> | {selectedEmails.size} sélectionnés</span>}
              </p>
            </div>
          </div>
        </div>

        {/* Stats */}
        {emailStats.total > 0 && (
          <div className="grid grid-cols-3 gap-3 mb-4">
            <div className="bg-white rounded-lg p-3 text-center border border-green-200">
              <div className="flex items-center justify-center gap-1 text-green-600 mb-1">
                <CheckCircle className="h-4 w-4" />
                <span className="text-lg font-bold">{emailStats.sent}</span>
              </div>
              <p className="text-xs text-slate-500">Envoyés</p>
            </div>
            <div className="bg-white rounded-lg p-3 text-center border border-red-200">
              <div className="flex items-center justify-center gap-1 text-red-600 mb-1">
                <XCircle className="h-4 w-4" />
                <span className="text-lg font-bold">{emailStats.failed}</span>
              </div>
              <p className="text-xs text-slate-500">Echoués</p>
            </div>
            <div className="bg-white rounded-lg p-3 text-center border border-amber-200">
              <div className="flex items-center justify-center gap-1 text-amber-600 mb-1">
                <Clock className="h-4 w-4" />
                <span className="text-lg font-bold">{emailStats.pending}</span>
              </div>
              <p className="text-xs text-slate-500">En attente</p>
            </div>
          </div>
        )}

        {/* Campaign status */}
        {campaign && (
          <div className="bg-white rounded-lg p-3 mb-4 border border-slate-200 text-sm">
            <span className="text-slate-500">Dernière campagne : </span>
            <span className={`font-semibold ${
              campaign.status === 'completed' ? 'text-green-600' :
              campaign.status === 'sending' ? 'text-amber-600' : 'text-slate-600'
            }`}>
              {campaign.status === 'completed' && `Terminée (${campaign.sent || 0} envoyés)`}
              {campaign.status === 'sending' && 'Envoi en cours...'}
            </span>
          </div>
        )}

        {/* Message */}
        {message && (
          <div className={`rounded-lg p-3 mb-4 text-sm ${
            message.type === 'success' ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'
          }`} data-testid="campaign-message">
            {message.text}
          </div>
        )}

        {/* Add guest form */}
        <div className="flex gap-2 mb-4">
          <Button
            variant="outline"
            onClick={() => setShowAddForm(!showAddForm)}
            className="border-green-300 text-green-700 hover:bg-green-50"
            data-testid="add-guest-toggle"
          >
            <Plus className="h-4 w-4 mr-2" />
            Ajouter un invité
          </Button>
        </div>

        {showAddForm && (
          <form onSubmit={addGuest} className="mb-4 bg-white rounded-lg border border-slate-200 p-4 space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <input
                type="text"
                placeholder="Nom complet *"
                value={newGuest.name}
                onChange={e => setNewGuest(prev => ({ ...prev, name: e.target.value }))}
                required
                className="px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:border-blue-400"
                data-testid="add-guest-name"
              />
              <input
                type="email"
                placeholder="Email *"
                value={newGuest.email}
                onChange={e => setNewGuest(prev => ({ ...prev, email: e.target.value }))}
                required
                className="px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:border-blue-400"
                data-testid="add-guest-email"
              />
              <input
                type="text"
                placeholder="Profession (optionnel)"
                value={newGuest.profession}
                onChange={e => setNewGuest(prev => ({ ...prev, profession: e.target.value }))}
                className="px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:border-blue-400"
                data-testid="add-guest-profession"
              />
            </div>
            <Button
              type="submit"
              disabled={addingGuest || !newGuest.name || !newGuest.email}
              className="bg-green-600 hover:bg-green-700 text-white"
              data-testid="add-guest-submit"
            >
              {addingGuest ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <UserPlus className="h-4 w-4 mr-2" />}
              Ajouter + créer le compte
            </Button>
          </form>
        )}

        {/* Toggle guest list */}
        <Button
          variant="outline"
          onClick={() => setShowGuests(!showGuests)}
          className="w-full mb-4 justify-between border-slate-300"
          data-testid="toggle-guests-btn"
        >
          <span>Liste des invités ({guests.length})</span>
          {showGuests ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </Button>

        {/* Guest list with checkboxes */}
        {showGuests && (
          <div className="mb-4 bg-white rounded-lg border border-slate-200 overflow-hidden">
            {/* Search + Select all */}
            <div className="p-3 border-b border-slate-200 flex items-center gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <input
                  type="text"
                  placeholder="Rechercher..."
                  value={searchFilter}
                  onChange={e => setSearchFilter(e.target.value)}
                  className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:border-blue-400"
                  data-testid="guest-search"
                />
              </div>
              <Button variant="outline" size="sm" onClick={selectAll} data-testid="select-all-btn">
                {selectedEmails.size === filteredGuests.length ? 'Désélectionner' : 'Tout sélectionner'}
              </Button>
            </div>

            {/* Guest rows */}
            <div className="max-h-72 overflow-y-auto">
              {filteredGuests.map((g, i) => (
                editingGuest === g.email ? (
                  <div key={g.email} className="flex items-center gap-2 px-3 py-2 bg-blue-50 border-b border-slate-100">
                    <input type="text" value={editForm.name} onChange={e => setEditForm(p => ({...p, name: e.target.value}))} className="flex-1 px-2 py-1 text-sm border rounded" placeholder="Nom" />
                    <input type="email" value={editForm.email} onChange={e => setEditForm(p => ({...p, email: e.target.value}))} className="flex-1 px-2 py-1 text-sm border rounded" placeholder="Email" />
                    <Button size="sm" className="bg-green-600 text-white h-7 px-2 text-xs" onClick={() => saveEdit(g.email)}>OK</Button>
                    <Button size="sm" variant="outline" className="h-7 px-2 text-xs" onClick={() => setEditingGuest(null)}>X</Button>
                  </div>
                ) : (
                <label
                  key={g.email}
                  className={`flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-slate-50 border-b border-slate-100 last:border-0 ${
                    selectedEmails.has(g.email) ? 'bg-blue-50' : ''
                  }`}
                  data-testid={`guest-row-${i}`}
                  onClick={(e) => { e.preventDefault(); toggleEmail(g.email); }}
                >
                  <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                    selectedEmails.has(g.email) ? 'bg-blue-600 border-blue-600' : 'border-slate-300'
                  }`}>
                    {selectedEmails.has(g.email) && <Check className="h-3 w-3 text-white" />}
                  </div>
                  <input
                    type="checkbox"
                    className="sr-only"
                    checked={selectedEmails.has(g.email)}
                    onChange={() => toggleEmail(g.email)}
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-900 truncate">{g.name}</p>
                    <p className="text-xs text-slate-500 truncate">{g.email}</p>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {g.profession && <span className="text-xs text-slate-400 hidden sm:inline truncate max-w-[150px]">{g.profession}</span>}
                    {g.has_account ? (
                      <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full whitespace-nowrap">Compte</span>
                    ) : (
                      <span className="text-xs bg-slate-100 text-slate-500 px-2 py-0.5 rounded-full whitespace-nowrap">Pas de compte</span>
                    )}
                    <button onClick={(e) => { e.preventDefault(); e.stopPropagation(); startEdit(g); }} className="p-1 text-slate-300 hover:text-blue-500 transition-colors">
                      <Pencil className="h-3.5 w-3.5" />
                    </button>
                    <button
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); deleteGuest(g.email, g.name); }}
                      className="p-1 text-slate-300 hover:text-red-500 transition-colors"
                      data-testid={`delete-guest-${i}`}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </label>
                )
              ))}
            </div>

            {filteredGuests.length === 0 && (
              <p className="p-4 text-center text-sm text-slate-500">Aucun invité trouvé</p>
            )}
          </div>
        )}

        {/* Actions */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Button
            onClick={createAccounts}
            disabled={creatingAccounts}
            className="bg-indigo-600 hover:bg-indigo-700 text-white justify-start"
            data-testid="create-accounts-btn"
          >
            {creatingAccounts ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <UserPlus className="h-4 w-4 mr-2" />}
            Créer les comptes {selectedEmails.size > 0 ? `(${selectedEmails.size})` : '(tous)'}
          </Button>

          <Button
            onClick={sendNow}
            disabled={sending || isSending}
            className="bg-blue-600 hover:bg-blue-700 text-white justify-start"
            data-testid="send-now-btn"
          >
            {(sending || isSending) ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
            Envoyer {selectedEmails.size > 0 ? `(${selectedEmails.size})` : '(tous)'}
          </Button>

          {emailStats.failed > 0 && (
            <Button
              onClick={retryFailed}
              disabled={retrying}
              variant="outline"
              className="border-red-300 text-red-700 hover:bg-red-50 justify-start"
              data-testid="retry-failed-btn"
            >
              {retrying ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
              Renvoyer les échoués ({emailStats.failed})
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default LaunchCampaignPanel;
